import { create } from 'zustand'

const NOTIFICATION_TIMEOUT = 5000
const MAX_NOTIFICATIONS = 7

export type NotificationType = 'success' | 'error' | 'warning' | 'info'

export interface Notification {
  id: number
  type: NotificationType
  message: string
  timeout: number
}

export interface NotificationInput {
  type?: NotificationType
  message: string
  timeout?: number
}

interface NotificationState {
  notifications: Notification[]
  addNotification: (notification: NotificationInput) => void
  removeNotification: (id: number) => void
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification: NotificationInput) => {
    const id = Date.now()
    const newNotification: Notification = {
      id,
      type: notification.type || 'info',
      message: notification.message,
      timeout: notification.timeout || NOTIFICATION_TIMEOUT,
    }

    set((state: NotificationState) => ({
      notifications: [...state.notifications.slice(-MAX_NOTIFICATIONS + 1), newNotification],
    }))

    setTimeout(() => {
      set((state: NotificationState) => ({
        notifications: state.notifications.filter((n: Notification) => n.id !== id),
      }))
    }, newNotification.timeout)
  },

  removeNotification: (id: number) => {
    set((state: NotificationState) => ({
      notifications: state.notifications.filter((n: Notification) => n.id !== id),
    }))
  },
}))

export const notificationService = {
  success: (message: string, timeout = NOTIFICATION_TIMEOUT) => {
    useNotificationStore.getState().addNotification({
      type: 'success',
      message,
      timeout,
    })
  },

  error: (message: string, timeout = NOTIFICATION_TIMEOUT) => {
    useNotificationStore.getState().addNotification({
      type: 'error',
      message,
      timeout,
    })
  },

  warning: (message: string, timeout = NOTIFICATION_TIMEOUT) => {
    useNotificationStore.getState().addNotification({
      type: 'warning',
      message,
      timeout,
    })
  },

  info: (message: string, timeout = NOTIFICATION_TIMEOUT) => {
    useNotificationStore.getState().addNotification({
      type: 'info',
      message,
      timeout,
    })
  },
}
